import React from 'react';
import PropTypes from 'prop-types';
import classNames from 'classnames';
import { withLocalize } from 'react-localize-redux';
import Button from '../../../components/atoms/Button';
import Icon, { IconSizes, IconVariants } from '../../../components/foundations/Icon';
import { artifactPagePath, parseArtifactContentUrl } from '../../../artifactPage/artifactPageUrl';
import { CHAT_EVENTS, trackChatEvent } from '../../constants';
import { artifactEventParams } from '../../htmlArtifacts';
import { useHtmlArtifactShelf } from './htmlArtifactShelfContext';
import css from './HtmlArtifactLink.css';

const TRANSLATION_PREFIX = 'STATIC.PAGES.ANALYSIS_CHAT.HTML_ARTIFACT';

const HtmlArtifactLink = ({ artifact, translate }) => {
    const shelf = useHtmlArtifactShelf();
    const parsed = parseArtifactContentUrl(artifact.content_url);
    const title = artifact.title || translate(`${TRANSLATION_PREFIX}.TITLE_FALLBACK`);

    const entry = parsed && shelf?.artifacts.find(candidate => candidate.id === parsed.artifactId);
    const isShowing = Boolean(parsed) && shelf?.openArtifactId === parsed.artifactId;

    const trackOpen = inNewTab =>
        trackChatEvent(CHAT_EVENTS.OPEN_HTML_ARTIFACT, {
            ...artifactEventParams(artifact.content_url),
            inNewTab,
        });

    const showInShelf = () => {
        trackOpen(false);
        shelf.showArtifact(parsed.artifactId);
    };

    const renderAction = () => {
        if (!parsed) return null;
        if (isShowing) return <span className={css.badge}>{translate(`${TRANSLATION_PREFIX}.SHOWING`)}</span>;
        if (shelf) {
            return (
                <Button type="link" className={css.action} onClick={showInShelf}>
                    {translate(`${TRANSLATION_PREFIX}.OPEN_SHORT`)}
                </Button>
            );
        }
        return (
            <a
                className={css.action}
                href={artifactPagePath(artifact.content_url)}
                target="_blank"
                rel="noopener noreferrer"
                onClick={() => trackOpen(true)}
            >
                {translate(`${TRANSLATION_PREFIX}.OPEN`)}
            </a>
        );
    };

    const meta = () => {
        if (!parsed) return translate(`${TRANSLATION_PREFIX}.UNAVAILABLE`);
        if (!shelf) return translate(`${TRANSLATION_PREFIX}.OPEN_HINT`);
        const kind = translate(`${TRANSLATION_PREFIX}.KIND`);
        return entry?.time ? `${kind} · ${entry.time}` : kind;
    };

    return (
        <div className={classNames(css.card, { [css.showing]: isShowing })}>
            <div className={classNames(css.tile, { [css.tileShowing]: isShowing })}>
                <Icon
                    name="file"
                    size={IconSizes.SMALL}
                    variant={isShowing ? IconVariants.WHITE : IconVariants.DARK}
                    interactive={false}
                />
            </div>
            <div className={css.body}>
                <span className={css.title}>{title}</span>
                <span className={css.meta}>{meta()}</span>
            </div>
            {renderAction()}
        </div>
    );
};

HtmlArtifactLink.propTypes = {
    artifact: PropTypes.shape({
        title: PropTypes.string,
        content_url: PropTypes.string,
    }).isRequired,
    translate: PropTypes.func.isRequired,
};

export default withLocalize(HtmlArtifactLink);
